import React, { useState } from "react";
import { Link } from "react-router-dom";

// ── useIsMobile hook ──────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return isMobile;
};

// ── Accordion Section (mobile only) ──────────────────────
const AccordionSection = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "12px 0",
          color: "#fff",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700 }}>{title}</span>
        <span style={{
          fontSize: 20, fontWeight: 300,
          transition: "transform 0.25s",
          transform: open ? "rotate(45deg)" : "rotate(0deg)",
          color: "rgba(255,255,255,0.7)",
        }}>+</span>
      </button>
      <div style={{
        overflow: "hidden",
        maxHeight: open ? "300px" : "0px",
        transition: "max-height 0.3s ease",
      }}>
        <div style={{ paddingBottom: 14 }}>{children}</div>
      </div>
    </div>
  );
};

// ── Shared data ───────────────────────────────────────────
const quickLinks = [
  { name: "Home",     path: "/" },
  { name: "Services", path: "/services" },
  { name: "AI Tools", path: "/ai-tools" },
  { name: "Blog",     path: "/blog" },
];
const services = ["Oracle Cloud", "AI Solutions", "Data Analytics", "IT Consulting"];

const linkStyle = {
  color: "rgba(255,255,255,0.8)",
  fontSize: 13,
  textDecoration: "none",
  display: "block",
  padding: "4px 0",
};

// ── FOOTER ────────────────────────────────────────────────
function Footer() {
  const isMobile = useIsMobile();

  return (
    <footer className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-8">

      {isMobile ? (
        // ══════════════════════════════════════════
        // MOBILE — compact logo row + accordion
        // ══════════════════════════════════════════
        <div style={{ padding: "0 20px" }}>

          {/* Logo + Name + Address in one row */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            paddingBottom: 16,
            borderBottom: "1px solid rgba(255,255,255,0.15)",
            marginBottom: 4,
          }}>
            <img
              src={process.env.PUBLIC_URL + "/metavix-logo-transparent.png"}
              alt="MetaVix Solutions"
              style={{
                height: "36px", objectFit: "contain",
                flexShrink: 0, filter: "brightness(0) invert(1)",
              }}
            />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
                MetaVix Solutions
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
                6165 Sweetbay Ln, Alpharetta, GA 30004, USA
              </div>
            </div>
          </div>

          {/* Accordions */}
          <AccordionSection title="Quick Links">
            {quickLinks.map(l => (
              <Link key={l.name} to={l.path} style={linkStyle}>{l.name}</Link>
            ))}
          </AccordionSection>

          <AccordionSection title="Services">
            {services.map(s => (
              <Link key={s} to="/services" style={linkStyle}>{s}</Link>
            ))}
          </AccordionSection>

          <AccordionSection title="Contact">
            <p style={{ ...linkStyle, margin: 0 }}>info@metavixsolutions.com</p>
            <p style={{ ...linkStyle, margin: 0 }}>+1 (407) 982-4262</p>
          </AccordionSection>

          {/* Copyright */}
          <div style={{
            borderTop: "1px solid rgba(255,255,255,0.15)",
            marginTop: 0, padding: "14px 0",
            textAlign: "center", fontSize: 12,
            color: "rgba(255,255,255,0.65)",
          }}>
            © 2026 MetaVix Solutions. All rights reserved.
          </div>

        </div>

      ) : (
        // ══════════════════════════════════════════
        // DESKTOP — your exact original layout
        // ══════════════════════════════════════════
        <>
          <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-5 px-6">

            {/* COMPANY */}
            <div>
              <div className="mb-4" style={{ textAlign: "center", marginTop: "-32px", marginLeft: "-16px" }}>
                <img
                  src={process.env.PUBLIC_URL + "/metavix-logo-footer.png"}
                  alt="MetaVix Solutions"
                  style={{
                    height: "80px", width: "80px",
                    objectFit: "contain",
                    filter: "brightness(0) invert(1)",
                    display: "block", margin: "0 auto",
                  }}
                />
                <h3 className="text-white font-bold text-2xl">MetaVix Solutions</h3>
                <p className="text-white text-base mt-2">
                  6165 Sweetbay Ln<br />
                  Alpharetta, GA 30004, USA
                </p>
              </div>
            </div>

            {/* QUICK LINKS */}
            <div>
              <h4 className="text-white font-bold text-base mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-white text-sm hover:text-blue-200 transition">Home</Link></li>
                <li><Link to="/services" className="text-white text-sm hover:text-blue-200 transition">Services</Link></li>
                <li><Link to="/ai-tools" className="text-white text-sm hover:text-blue-200 transition">AI Tools</Link></li>
                <li><Link to="/blog" className="text-white text-sm hover:text-blue-200 transition">Blog</Link></li>
              </ul>
            </div>

            {/* SERVICES */}
            <div>
              <h4 className="text-white font-bold text-base mb-4">Services</h4>
              <ul className="space-y-2">
                <li><Link to="/services" className="text-white text-sm hover:text-blue-200 transition">Oracle Cloud</Link></li>
                <li><Link to="/services" className="text-white text-sm hover:text-blue-200 transition">AI Solutions</Link></li>
                <li><Link to="/services" className="text-white text-sm hover:text-blue-200 transition">Data Analytics</Link></li>
                <li><Link to="/services" className="text-white text-sm hover:text-blue-200 transition">IT Consulting</Link></li>
              </ul>
            </div>

            {/* CONTACT */}
            <div>
              <h4 className="text-white font-bold text-base mb-4">Contact</h4>
              <p className="text-white text-base mb-2">info@metavixsolutions.com</p>
              <p className="text-white text-base">+1 (407) 982-4262</p>
            </div>

          </div>

          {/* COPYRIGHT */}
          <div className="max-w-6xl mx-auto px-6 mt-6 pt-4 border-t border-blue-700 text-center text-white text-sm">
            © 2026 MetaVix Solutions. All rights reserved.
          </div>
        </>
      )}

    </footer>
  );
}

export default Footer;